'use client';

import { ButtonBase, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, useId } from 'react';
import type { TSelectableEntityType } from '../../EntityTypeSelector/EntityTypeSelector/entityTypeGroups';
import {
  EntityInitialsAvatar,
  type RequestIdentityState,
} from '../EntityInitialsAvatar/EntityInitialsAvatar';
import { identityCardWidth } from '../shared/identityCardWidth';
import { identityOptionSurface } from '../shared/identityOptionSurface';
import { SelectedPill } from '../shared/SelectedPill';
import { REQUEST_IDENTITY_CARD_COPY } from './requestIdentityCardCopy';

export type { RequestIdentityState };

export type RequestIdentityStatusLabels = Partial<
  Record<Exclude<RequestIdentityState, 'shared'>, string>
>;

export type RequestIdentityCardProps = {
  id: string;
  /** The entity's full name. It is always the card's accessible name, even when it is clamped. */
  name: string;
  entityType: TSelectableEntityType;
  state: RequestIdentityState;
  /** Whether this card is the chosen one. The parent owns the choice. */
  selected: boolean;
  /** Reports the card's id. Never called for a `shared` card. */
  onSelect: (id: string) => void;
  /** The status text on a `shared` card. */
  sharedLabel?: string;
  /** Status words for the other states. */
  statusLabels?: RequestIdentityStatusLabels;
  /** Text of the pill on a chosen card. */
  selectedLabel?: string;
  /** Lets a parent radio group manage the tab stop. Defaults to the browser's own. */
  tabIndex?: 0 | -1;
  'data-testid'?: string;
};

// person-api's entity type codes arrive as `Private_Company`; the record attribute is kebab case.
function toRecordType(entityType: TSelectableEntityType): string {
  return entityType.toLowerCase().replace(/_/g, '-');
}

function resolveStatusText({
  state,
  sharedLabel,
  statusLabels,
}: Readonly<{
  state: RequestIdentityState;
  sharedLabel: string;
  statusLabels?: RequestIdentityStatusLabels;
}>): string {
  if (state === 'shared') {
    return sharedLabel;
  }
  return statusLabels?.[state] ?? REQUEST_IDENTITY_CARD_COPY[state];
}

/**
 * One identity the customer holds, as a choice in a request. A radio that reports its id when
 * tapped.
 *
 * An already-shared identity is shown dimmed, stays focusable and is announced as unavailable
 * (`aria-disabled`), and never reports a tap. The component holds no rule about which identity
 * should be chosen: the parent sets `selected`.
 */
function RequestIdentityCardComponent({
  id,
  name,
  entityType,
  state,
  selected,
  onSelect,
  sharedLabel = REQUEST_IDENTITY_CARD_COPY.shared,
  statusLabels,
  selectedLabel = REQUEST_IDENTITY_CARD_COPY.selected,
  tabIndex,
  'data-testid': dataTestId,
}: Readonly<RequestIdentityCardProps>): ReactElement {
  const nameId = useId();
  const statusId = useId();
  const isShared = state === 'shared';
  const isChosen = selected && !isShared;
  const statusText = resolveStatusText({ state, sharedLabel, statusLabels });

  const handleClick = () => {
    if (isShared) {
      return;
    }
    onSelect(id);
  };

  return (
    <ButtonBase
      role="radio"
      aria-checked={isChosen}
      aria-disabled={isShared}
      aria-labelledby={nameId}
      aria-describedby={statusId}
      disableRipple
      tabIndex={tabIndex}
      onClick={handleClick}
      data-testid={dataTestId}
      data-record-id={id}
      data-record-type={toRecordType(entityType)}
      data-record-status={state}
      sx={(theme: Theme) => ({
        ...identityOptionSurface(theme, { selected: isChosen, available: !isShared }),
        flex: 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'flex-start',
        // The card is fixed at 140px and the Selected pill is absolutely positioned across its
        // bottom edge. What limits the layout is the clearance between the status word and the
        // pill: with a three-line name it only holds with these tight gaps and no bottom padding.
        // A shorter name leaves the room underneath.
        gap: theme.semantic.spacing['2xs'],
        width: identityCardWidth(theme),
        height: theme.identityCard.height,
        paddingTop: theme.semantic.spacing.sm,
        paddingInline: theme.semantic.spacing.sm,
        textAlign: 'center' as const,
        cursor: isShared ? 'default' : 'pointer',
        opacity: isShared ? theme.identityCard.unavailableOpacity : 1,
      })}
    >
      <EntityInitialsAvatar name={name} state={state} />
      <Typography
        id={nameId}
        variant="body2"
        component="span"
        sx={{
          maxWidth: '100%',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          overflowWrap: 'anywhere',
        }}
      >
        {name}
      </Typography>
      <Typography
        id={statusId}
        variant="caption"
        component="span"
        sx={(theme: Theme) => ({
          lineHeight: theme.semantic.lineHeight.single,
          color: state === 'verified' ? theme.palette.text.success : theme.palette.text.tertiary,
        })}
      >
        {statusText}
      </Typography>
      {isChosen && <SelectedPill label={selectedLabel} />}
    </ButtonBase>
  );
}

export const RequestIdentityCard = withClarityTheme(RequestIdentityCardComponent);
